import { Building2, Heart, Mail, MapPin, Phone, Pill } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Container from '../../common/Container';

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPharmacy = location.pathname.startsWith('/pharmacy');

  const scrollTo = (id: string, targetPath?: string) => {
    const target = targetPath || (isPharmacy ? '/pharmacy' : '/');

    if (location.pathname !== target) {
      navigate(target);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-950 text-slate-400 pt-16 pb-12 border-t border-slate-800">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-slate-800/80">
          {/* Col 1: Brand Info */}
          <div className="lg:col-span-4 space-y-4">
            <Link
              to={isPharmacy ? '/pharmacy' : '/'}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3"
            >
              <div className="p-1.5 rounded-xl bg-white/10 border border-white/20">
                <img
                  src="/img/svg/hms_logo.png"
                  alt="HMS Logo"
                  className="h-9 w-auto object-contain brightness-110"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-white tracking-tight">
                  Divine <span className="text-[#38bcd8]">{isPharmacy ? 'Pharmacy' : 'HMS'}</span>
                </span>
                <p className="text-[11px] text-slate-400">
                  {isPharmacy ? 'Pharmacy ERP & Inventory Suite' : 'Hospital Management ERP Suite'}
                </p>
              </div>
            </Link>

            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              {isPharmacy
                ? 'High-speed counter dispensing, batch-wise expiry tracking, automated purchase requisitions, and compliant GST reporting for retail medical shops, chemist outlets, and pharmacies.'
                : 'Empowering healthcare facilities with comprehensive electronic medical records, real-time bed management, intuitive OPD billing, and intelligent clinical analytics.'}
            </p>

            <div className="pt-2 text-xs text-slate-400">
              Developed & Supported by{' '}
              <strong className="text-white">Devdeep Systems Pvt. Ltd.</strong>
            </div>
          </div>

          {/* Col 2: Dynamic Modules based on Page */}
          <div className="lg:col-span-3 space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              {isPharmacy ? 'Pharmacy Modules' : 'Hospital Modules'}
            </div>
            <ul className="space-y-2 text-sm">
              {isPharmacy ? (
                <>
                  <li>
                    <button
                      onClick={() => scrollTo('dashboard')}
                      className="hover:text-white transition-colors"
                    >
                      Pharmacy Command Center
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('create-invoice')}
                      className="hover:text-white transition-colors"
                    >
                      POS Counter Billing
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('sales-invoice')}
                      className="hover:text-white transition-colors"
                    >
                      Sales Invoice Register
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('stock-inventory')}
                      className="hover:text-white transition-colors"
                    >
                      Batch Stock Inventory
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('add-stock')}
                      className="hover:text-white transition-colors"
                    >
                      Inward GRN / Add Stock
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('purchase-order')}
                      className="hover:text-white transition-colors"
                    >
                      Purchase Orders (PO)
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('return-medicine')}
                      className="hover:text-white transition-colors"
                    >
                      Returns & Credit Notes
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('master-catalog')}
                      className="hover:text-white transition-colors"
                    >
                      Master Catalog Setup
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <button
                      onClick={() => scrollTo('dashboard')}
                      className="hover:text-white transition-colors"
                    >
                      Centralized Dashboard
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('patient-registration')}
                      className="hover:text-white transition-colors"
                    >
                      Patient Registration
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('opd')}
                      className="hover:text-white transition-colors"
                    >
                      OPD & Fast Billing
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('ipd')}
                      className="hover:text-white transition-colors"
                    >
                      IPD Ward Operations
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('atd')}
                      className="hover:text-white transition-colors"
                    >
                      Bed Management (ATD)
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollTo('discharge')}
                      className="hover:text-white transition-colors"
                    >
                      Discharge Documentation
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Col 3: Navigation & System Switcher */}
          <div className="lg:col-span-2 space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              Navigation
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/"
                  className={`hover:text-white transition-colors inline-flex items-center gap-1.5 ${
                    !isPharmacy ? 'text-[#38bcd8] font-medium' : ''
                  }`}
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Hospital ERP</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/pharmacy"
                  className={`hover:text-white transition-colors inline-flex items-center gap-1.5 ${
                    isPharmacy ? 'text-[#38bcd8] font-medium' : ''
                  }`}
                >
                  <Pill className="w-3.5 h-3.5" />
                  <span>Pharmacy ERP</span>
                </Link>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('about')}
                  className="hover:text-white transition-colors"
                >
                  Overview
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('features')}
                  className="hover:text-white transition-colors"
                >
                  Key Benefits
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('reports')}
                  className="hover:text-white transition-colors"
                >
                  Audit & Reports
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('contact')}
                  className="hover:text-white transition-colors"
                >
                  Request Live Demo
                </button>
              </li>
              <li>
                <a
                  href="https://devdeepsystems.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  Corporate Site
                </a>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact & Socials */}
          <div className="lg:col-span-3 space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              Contact & Address
            </div>
            <div className="space-y-2.5 text-xs text-slate-400">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#38bcd8] flex-shrink-0 mt-0.5" />
                <span>
                  Floor no. 2, Q.N.02, Street no 03, Krishna Nagar, Devpuri, Raipur, CG, India
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#38bcd8] flex-shrink-0" />
                <a href="tel:9752002111" className="hover:text-white transition-colors">
                  +91 9752002111 / 7987702243
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#38bcd8] flex-shrink-0" />
                <a
                  href="mailto:kuldip@devdeepsystems.com"
                  className="hover:text-white transition-colors"
                >
                  kuldip@devdeepsystems.com
                </a>
              </div>
            </div>

            {/* Social media links */}
            <div className="pt-3">
              <div className="text-xs uppercase font-medium text-slate-400 mb-2">
                Connect with us
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="https://www.linkedin.com/company/devdeep-systems-pvt-ltd/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="p-2 rounded-xl bg-slate-900 hover:bg-[#0D7893] text-slate-300 hover:text-white border border-slate-800 transition-all"
                >
                  <img
                    src="/img/svg/linkedin.svg"
                    alt="LinkedIn"
                    className="w-4 h-4 invert opacity-80"
                  />
                </a>
                <a
                  href="mailto:kuldip@devdeepsystems.com"
                  aria-label="Gmail"
                  className="p-2 rounded-xl bg-slate-900 hover:bg-[#0D7893] text-slate-300 hover:text-white border border-slate-800 transition-all"
                >
                  <img src="/img/svg/gmail.png" alt="Email" className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=100089759592369"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="p-2 rounded-xl bg-slate-900 hover:bg-[#0D7893] text-slate-300 hover:text-white border border-slate-800 transition-all"
                >
                  <img src="/img/svg/facebook.png" alt="Facebook" className="w-4 h-4" />
                </a>
                <a
                  href="https://instagram.com/kuldip_mahendra?igshid=bmpoOWI3NmtkdDFz"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="p-2 rounded-xl bg-slate-900 hover:bg-[#0D7893] text-slate-300 hover:text-white border border-slate-800 transition-all"
                >
                  <img src="/img/svg/instagram.png" alt="Instagram" className="w-4 h-4" />
                </a>
                <a
                  href="https://youtube.com/@CoachKuldipMahendra?si=ihcfS6Y99evkvvIo"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="p-2 rounded-xl bg-slate-900 hover:bg-[#0D7893] text-slate-300 hover:text-white border border-slate-800 transition-all"
                >
                  <img src="/img/svg/youtube.png" alt="YouTube" className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} Devdeep Systems Pvt. Ltd. All rights reserved.</div>
          <div className="flex items-center gap-1">
            <span>Crafted with</span>
            <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
            <span>for healthcare excellence</span>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
